import { FOOTPRINT_DEFAULT_CELL } from "../compile/footprint-cell.ts";
import type { FootprintDraftState } from "./footprint-drafts.ts";

/**
 * A footprint draft's cell keys, `column,rowsUp`, in cells of another side (ADR-113 decision 8). A draft says the side
 * in pixels its keys are counted in in `cell`; one that says nothing was drawn at the authoring cell.
 */

/**
 * Doubles cell keys into the cells of a finer side: each cell becomes the `factor` by `factor` cells that cover it.
 *
 * @param keys The cell keys.
 * @param factor How many finer cells fit along one coarser cell.
 * @returns The finer cells' keys, in the coarser cells' order.
 */
function finer(keys: readonly string[], factor: number): string[] {
  const out: string[] = [];
  for (const key of keys) {
    const [column, row] = key.split(",").map(Number) as [number, number];
    for (let dy = 0; dy < factor; dy += 1) {
      for (let dx = 0; dx < factor; dx += 1) {
        out.push(`${column * factor + dx},${row * factor + dy}`);
      }
    }
  }
  return out;
}

/**
 * A draft counted in finer cells: the cells it blocks, its passage and its Behind and In front cells become the same
 * area in cells of `to` pixels, exactly, and the draft names `to`. A draft already at `to` is returned as it is.
 *
 * @param state The draft.
 * @param to The side in pixels wanted, which divides the draft's own.
 * @returns The draft at `to`.
 * @throws RangeError when `to` is coarser than the draft's cell or does not divide it.
 */
export function draftAtCell(state: FootprintDraftState, to: number): FootprintDraftState {
  const from = state.cell ?? FOOTPRINT_DEFAULT_CELL;
  if (from === to) {
    return state.cell === undefined && to !== FOOTPRINT_DEFAULT_CELL ? { ...state, cell: to } : state;
  }
  if (to > from || from % to !== 0) {
    throw new RangeError(`a draft in ${from} px cells cannot be exactly counted in ${to} px cells`);
  }
  const factor = from / to;
  return {
    ...state,
    cells: finer(state.cells, factor),
    ...(state.passage === undefined ? {} : { passage: finer(state.passage, factor) }),
    ...(state.stand === undefined ? {} : { stand: { behind: finer(state.stand.behind, factor), front: finer(state.stand.front, factor) } }),
    cell: to,
  };
}
